import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { api } from './api';
import Login from './pages/Login';
import CambiarPassword from './pages/CambiarPassword';
import NuevaSolicitud from './pages/NuevaSolicitud';
import Historial from './pages/Historial';
import NuevaReembolso from './pages/NuevaReembolso';
import HistorialReembolsos from './pages/HistorialReembolsos';
import NuevaFiniquito from './pages/NuevaFiniquito';
import HistorialFiniquitos from './pages/HistorialFiniquitos';
import Admin from './pages/Admin';
import Reporte from './pages/Reporte';
import NuevaComprobacion from './pages/NuevaComprobacion';
import HistorialComprobaciones from './pages/HistorialComprobaciones';
import CapturaComprobantes from './pages/CapturaComprobantes';
import Dashboard from './pages/Dashboard';
import MiDashboard from './pages/MiDashboard';
import DireccionGeneral from './pages/DireccionGeneral';
import Comparativos from './pages/Comparativos';
import Papelera from './pages/Papelera';
import NuevaRequisicion from './pages/NuevaRequisicion';
import HistorialRequisiciones from './pages/HistorialRequisiciones';
import CatalogoMateriales from './pages/CatalogoMateriales';
import Presupuestos from './pages/Presupuestos';
import CierrePagos from './pages/CierrePagos';
import AprobarRequisiciones from './pages/AprobarRequisiciones';
import ReporteRequisiciones from './pages/ReporteRequisiciones';
import DireccionesEntrega from './pages/DireccionesEntrega';
import PolizasContables from './pages/PolizasContables';
import CuentasBancarias from './pages/CuentasBancarias';
import ComprobantesMasivos from './pages/ComprobantesMasivos';
import HistorialGeneral from './pages/HistorialGeneral';
import AgentesIA from './pages/AgentesIA';
import { ROL_LABELS, Spinner } from './components/UI';
import { Icono, IconoMenu } from './components/Iconos';
import { TIPOS_NUEVA, navForUser, permitidoMenu } from './config/menu';

function leaves(nav) {
  return nav.flatMap(e => e.children ? e.children : [e]);
}


function BadgePendientes({ count }) {
  if (!count) return null;
  return <span className="nav-badge" aria-label={`${count} pendientes`}>{count > 99 ? '99+' : count}</span>;
}

function NavItem({ item, active, onClick, sub, badge }) {
  return (
    <button type="button" className="nav-item" aria-current={active ? 'page' : undefined} onClick={onClick}
      style={{ padding: sub ? '8px 12px 8px 14px' : '10px 12px' }}>
      <span className="nav-icon"><IconoMenu id={item.id} /></span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
      <BadgePendientes count={badge} />
    </button>
  );
}

// Pantallas ya migradas al tema Nixie. Las demás se muestran en un panel claro
// (.legacy-light) hasta que les toque su tanda.
const MIGRADAS = new Set([
  'dashboard',
  // tanda 2: captura de solicitudes, historiales y reporte
  'nueva', 'nueva-debito', 'nueva-contribuciones', 'nueva-activos-fijos', 'nueva-mercancias-nacionales',
  'nueva-nomina', 'nueva-complemento-importacion', 'nueva-caja-chica', 'nueva-reembolso-gastos',
  'historial-general', 'pagadas-contador', 'provision-contador', 'historial-comprobaciones', 'historial-requisiciones', 'reporte',
  // tanda 3: el resto de capturas, catálogos, administración y tableros
  'reembolso', 'finiquito', 'comprobacion', 'nueva-requisicion', 'captura-comprobantes', 'comparativo',
  'catalogo-materiales', 'presupuestos', 'borradas', 'admin', 'catalogos', 'direccion-general', 'mi-dashboard',
  'agentes-ia', 'cierre-pagos', 'aprobar-requisiciones', 'reporte-requisiciones', 'direcciones-entrega', 'polizas-contables', 'cuentas-bancarias', 'comprobantes-masivos',
]);

function Layout() {
  const { user, logout, loading, refreshUser } = useAuth();
  const [cambiandoRol, setCambiandoRol] = useState(false);

  const cambiarRol = async (rol) => {
    if (cambiandoRol || rol === user?.rol) return;
    setCambiandoRol(true);
    try {
      await api.post('/auth/cambiar-rol', { rol });
      await refreshUser();
    } catch (e) { /* silencioso */ }
    finally { setCambiandoRol(false); }
  };
  const [page, setPage] = useState('nueva');
  const [refreshKey, setRefreshKey] = useState(0);
  const [openGroups, setOpenGroups] = useState({});
  const [pendientes, setPendientes] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('nav_collapsed') === '1');
  const [windowW, setWindowW] = useState(window.innerWidth);
  const [nuevaModal, setNuevaModal] = useState(false);
  // Esc cierra el selector de tipo de solicitud
  useEffect(() => {
    if (!nuevaModal) return undefined;
    const tecla = (e) => { if (e.key === 'Escape') setNuevaModal(false); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [nuevaModal]);
  const toggleCollapse = () => setCollapsed(c => { const next = !c; localStorage.setItem('nav_collapsed', next ? '1' : '0'); return next; });
  useEffect(() => {
    const handle = () => setWindowW(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  useEffect(() => {
    if (!user) return;
    const cargar = () => api.get('/pendientes').then(setPendientes).catch(() => {});
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [user, page, refreshKey]);

  // Avisos del administrador pendientes de reconocer (modal al iniciar sesión)
  const [avisosPendientes, setAvisosPendientes] = useState([]);
  useEffect(() => {
    if (!user || user.requiereCambioPassword) return;
    api.get('/avisos/pendientes').then(setAvisosPendientes).catch(() => {});
  }, [user]);
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  const marcarAvisoVisto = async () => {
    const aviso = avisosPendientes[0];
    if (!aviso || enviandoAviso) return;
    setEnviandoAviso(true);
    try {
      await api.post(`/avisos/${aviso._id}/visto`);
      setAvisosPendientes(a => a.slice(1));
    } catch (e) { /* deja el aviso, se reintenta al recargar */ }
    finally { setEnviandoAviso(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--chassis)' }}>
      <Spinner />
    </div>
  );
  if (!user) return <Login />;
  if (user.requiereCambioPassword) return <CambiarPassword />;

  const nav = navForUser(user);
  const flat = leaves(nav);
  const currentPage = flat.find(n => n.id === page) ? page : flat[0]?.id;
  const toggleGroup = (g) => setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));
  const isMobile = windowW < 768;
  const handleNav = (id) => { setPage(id); if (isMobile) setSidebarOpen(false); };

  const colapsado = collapsed && !isMobile;
  const titulo = flat.find(n => n.id === currentPage)?.label || 'Solicitudes de pago';
  // Para programación 'cierre-pagos' no suma: repetiría lo que ya cuenta su bandeja de por pagar
  const pendientesTotal = Object.entries(pendientes || {}).filter(([k]) => !(k === 'cierre-pagos' && user?.rol === 'programacion')).reduce((s, [, n]) => s + (Number(n) || 0), 0);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>

      {/* Barra superior — solo en móvil */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          height: 54, display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px',
          background: 'var(--panel)', borderBottom: '1px solid var(--mesh)',
        }}>
          <button type="button" className="nx-ghost" aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(o => !o)}
            style={{ width: 38, height: 38, display: 'grid', placeItems: 'center', color: sidebarOpen ? 'var(--plasma)' : 'var(--glass)' }}>
            <Icono nombre={sidebarOpen ? 'cerrar' : 'menu'} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={`${process.env.PUBLIC_URL}/logo-selectshop.webp`} alt="Selectshop" width={128} height={19} style={{ display: 'block' }} />
            <span className="nx-label" style={{ fontSize: 12, color: 'var(--steel)' }}>Pagos</span>
          </div>
        </div>
      )}

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 149, background: 'rgba(17,17,17,0.4)' }} />
      )}

      {/* Menú — rack de canales */}
      <nav aria-label="Navegación principal" className="nav-rail" style={{
        width: isMobile ? 272 : colapsado ? 72 : 264,
        position: 'fixed', top: isMobile ? 54 : 0, bottom: 0, left: 0, zIndex: 150,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1), transform 0.26s cubic-bezier(0.4,0,0.2,1)',
      }}>

        {/* Marca + colapsar */}
        <div style={{ padding: colapsado ? '18px 0 16px' : '20px 16px 16px', borderBottom: '1px solid var(--mesh-soft)', display: 'flex', alignItems: 'center', justifyContent: colapsado ? 'center' : 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {/* Logotipo de selectshop.com.mx; colapsado, solo su símbolo */}
            {colapsado ? (
              <img src={`${process.env.PUBLIC_URL}/marca-selectshop.png`} alt="Selectshop" width={32} height={32} style={{ display: 'block' }} />
            ) : (
              <div style={{ minWidth: 0 }}>
                <img src={`${process.env.PUBLIC_URL}/logo-selectshop.webp`} alt="Selectshop" width={168} height={25} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />
                <div className="nx-label" style={{ fontSize: 11.5, marginTop: 6, color: 'var(--glass)' }}>Solicitudes de pago</div>
              </div>
            )}
          </div>
          {!isMobile && (
            <button type="button" className="nx-ghost" onClick={toggleCollapse} title={collapsed ? 'Expandir menú' : 'Colapsar menú'} aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
              style={{ width: 28, height: 28, display: colapsado ? 'none' : 'grid', placeItems: 'center', flexShrink: 0 }}>
              <Icono nombre="chevron" size={14} style={{ transform: 'rotate(180deg)' }} />
            </button>
          )}
        </div>

        {/* Acción principal */}
        {['admin', 'solicitante', 'finanzas'].includes(user.rol) && (
          <div style={{ padding: colapsado ? '12px 10px 4px' : '14px 14px 4px' }}>
            <button type="button" className="nx-plate" onClick={() => setNuevaModal(true)} title={colapsado ? 'Nueva solicitud' : undefined} aria-label="Nueva solicitud"
              style={colapsado ? { padding: '10px 0' } : undefined}>
              <Icono nombre="mas" size={16} stroke={2} />
              {!colapsado && 'Nueva solicitud'}
            </button>
          </div>
        )}

        {/* Canales */}
        <div style={{ padding: colapsado ? '10px 8px' : '12px 10px', flex: 1, overflowY: 'auto' }}>
          {colapsado && (
            <button type="button" className="nx-ghost" onClick={toggleCollapse} aria-label="Expandir menú" title="Expandir menú"
              style={{ width: '100%', height: 30, display: 'grid', placeItems: 'center', marginBottom: 8 }}>
              <Icono nombre="chevron" size={14} />
            </button>
          )}
          {nav.map(entry => {
            if (colapsado) {
              const id = entry.id || entry.group;
              const label = entry.label || entry.children?.[0]?.label || '';
              const isActive = entry.children ? entry.children.some(c => c.id === currentPage) : currentPage === entry.id;
              return (
                <button type="button" key={id} title={label} aria-label={label} className="nav-item" aria-current={isActive ? 'page' : undefined}
                  onClick={() => { if (entry.children) { toggleCollapse(); toggleGroup(entry.group); } else handleNav(entry.id); }}
                  style={{ justifyContent: 'center', padding: '10px 0' }}>
                  <span className="nav-icon"><IconoMenu id={id} size={19} /></span>
                </button>
              );
            }
            if (!entry.children) {
              return <NavItem key={entry.id} item={entry} active={currentPage === entry.id} badge={pendientes[entry.id]} onClick={() => handleNav(entry.id)} />;
            }
            const hasActive = entry.children.some(c => c.id === currentPage);
            const open = openGroups[entry.group] ?? hasActive;
            return (
              <div key={entry.group} style={{ margin: '18px 0 2px' }}>
                <button type="button" className="nav-group" aria-expanded={open} onClick={() => toggleGroup(entry.group)}>
                  <span>{entry.label}</span>
                  <Icono nombre="chevron" size={12} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                </button>
                {open && entry.children.map(child => (
                  <NavItem key={child.id} item={child} active={currentPage === child.id} sub badge={pendientes[child.id]} onClick={() => handleNav(child.id)} />
                ))}
              </div>
            );
          })}
        </div>

        {/* Operador */}
        {!colapsado && <div style={{ padding: '12px 12px 16px', borderTop: '1px solid var(--mesh-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '9px 10px', borderRadius: 4, background: 'var(--well)', border: '1px solid var(--mesh-soft)' }}>
            <div aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 3, flexShrink: 0, display: 'grid', placeItems: 'center', border: '1px solid var(--plasma-line)', color: 'var(--plasma)', fontFamily: 'var(--digit-font)', fontSize: 14 }}>
              {user.nombre?.charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.nombre}</div>
              <div className="nx-label" style={{ fontSize: 11.5, marginTop: 2 }}>{ROL_LABELS[user.rol]}</div>
            </div>
          </div>
          {(user.roles?.length > 0) && (
            <div style={{ marginBottom: 10 }} role="group" aria-label="Cambiar de rol">
              <div className="nx-label" style={{ fontSize: 11.5, marginBottom: 6, color: 'var(--glass)' }}>Cambiar de rol</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {[...new Set([user.rol, ...(user.roles || [])])].map(r => {
                  const activo = r === user.rol;
                  return (
                    <button type="button" key={r} onClick={() => cambiarRol(r)} disabled={activo || cambiandoRol} aria-pressed={activo}
                      title={activo ? 'Rol activo' : `Actuar como ${ROL_LABELS[r] || r}`}
                      style={{
                        padding: '4px 9px', borderRadius: 3, fontSize: 12, fontWeight: 600,
                        cursor: activo ? 'default' : 'pointer',
                        border: `1px solid ${activo ? 'var(--plasma)' : 'var(--mesh)'}`,
                        background: activo ? 'var(--plasma-dim)' : 'transparent',
                        color: activo ? 'var(--plasma)' : 'var(--glass)',
                        opacity: cambiandoRol && !activo ? 0.5 : 1,
                      }}>
                      {ROL_LABELS[r] || r}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <button type="button" className="nx-ghost" onClick={logout}
            style={{ width: '100%', padding: '8px 10px', fontSize: 12.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Icono nombre="salir" size={15} /> Cerrar sesión
          </button>
        </div>}
      </nav>

      {/* Área de trabajo */}
      <main className="app-main" style={{
        marginLeft: isMobile ? 0 : colapsado ? 72 : 264,
        flex: 1,
        padding: isMobile ? '70px 12px 24px' : '22px 28px 28px',
        minHeight: '100vh',
        position: 'relative', zIndex: 1,
        transition: 'margin-left 0.26s cubic-bezier(0.4,0,0.2,1)',
        boxSizing: 'border-box', minWidth: 0,
      }}>
        <header className="app-topbar">
          <div style={{ minWidth: 0 }}>
            <h1>{titulo}</h1>
          </div>
          <div className="app-profile">
            <span className="app-status" title="Solicitudes que esperan tu acción">
              <i aria-hidden="true" style={pendientesTotal ? { background: 'var(--plasma)', boxShadow: 'var(--glow)' } : undefined} />
              {pendientesTotal ? <>Te esperan <b style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma-hot)', fontWeight: 500, letterSpacing: 0 }}>{pendientesTotal}</b></> : 'Sin pendientes'}
            </span>
            <div className="app-avatar" aria-hidden="true">{user.nombre?.charAt(0).toUpperCase()}</div>
            <div>{user.nombre}<div className="nx-label" style={{ fontSize: 11.5, marginTop: 2 }}>{ROL_LABELS[user.rol]}</div></div>
          </div>
        </header>
        <div className={`app-content${MIGRADAS.has(currentPage) ? '' : ' legacy-light'}`}>
        {currentPage === 'dashboard'               && permitidoMenu(user, 'dashboard') && <Dashboard onNavigate={handleNav} />}
        {currentPage === 'mi-dashboard'            && permitidoMenu(user, 'mi-dashboard') && <MiDashboard />}
        {currentPage === 'direccion-general'       && permitidoMenu(user, 'direccion-general') && <DireccionGeneral onNavigate={handleNav} />}
        {currentPage === 'nueva'                   && <NuevaSolicitud key={refreshKey} onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'historial-general'        && <HistorialGeneral key={refreshKey} />}
        {currentPage === 'nueva-debito'            && <NuevaSolicitud key={`deb-${refreshKey}`} tipo="debito" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-contribuciones'    && <NuevaSolicitud key={`con-${refreshKey}`} tipo="contribuciones" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-activos-fijos'     && <NuevaSolicitud key={`af-${refreshKey}`} tipo="activos_fijos" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-mercancias-nacionales' && <NuevaSolicitud key={`mn-${refreshKey}`} tipo="mercancias_nacionales" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-nomina'            && <NuevaSolicitud key={`nom-${refreshKey}`} tipo="nomina" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-complemento-importacion' && <NuevaSolicitud key={`imp-${refreshKey}`} tipo="complemento_importacion" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-caja-chica'       && <NuevaSolicitud key={`cc-${refreshKey}`} tipo="caja_chica" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'nueva-reembolso-gastos' && <NuevaSolicitud key={`rg-${refreshKey}`} tipo="reembolso_gastos" onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'pagadas-contador'        && <Historial key={`pag-${refreshKey}`} vista={{ estado: 'pagada', titulo: 'Solicitudes Pagadas', subtitulo: 'todos los tipos de tus empresas', todosTipos: true, exportContabilidad: true }} />}
        {currentPage === 'provision-contador'      && <Historial key={`prov-${refreshKey}`} vista={{ estado: 'por_pagar', titulo: 'Para Provisión', subtitulo: 'por pagar de todos los tipos de tus empresas', todosTipos: true, exportContabilidad: true }} />}
        {currentPage === 'comparativo'             && <Comparativos />}
        {currentPage === 'reembolso'               && <NuevaReembolso key={refreshKey} onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'finiquito'               && <NuevaFiniquito key={refreshKey} onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'comprobacion'            && <NuevaComprobacion key={refreshKey} onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'historial-comprobaciones'&& <HistorialComprobaciones key={refreshKey} />}
        {currentPage === 'captura-comprobantes'    && <CapturaComprobantes />}
        {currentPage === 'nueva-requisicion'       && <NuevaRequisicion key={refreshKey} onCreated={() => setRefreshKey(k => k + 1)} />}
        {currentPage === 'historial-requisiciones' && <HistorialRequisiciones key={refreshKey} onNavigate={setPage} />}
        {currentPage === 'catalogo-materiales'     && <CatalogoMateriales />}
        {currentPage === 'presupuestos'            && permitidoMenu(user, 'presupuestos') && <Presupuestos />}
        {currentPage === 'cierre-pagos'            && permitidoMenu(user, 'cierre-pagos') && <CierrePagos />}
        {currentPage === 'aprobar-requisiciones'   && permitidoMenu(user, 'aprobar-requisiciones') && <AprobarRequisiciones onNavigate={handleNav} />}
        {currentPage === 'reporte-requisiciones'   && permitidoMenu(user, 'reporte-requisiciones') && <ReporteRequisiciones />}
        {currentPage === 'direcciones-entrega'     && permitidoMenu(user, 'direcciones-entrega') && <DireccionesEntrega />}
        {currentPage === 'comprobantes-masivos'    && permitidoMenu(user, 'comprobantes-masivos') && <ComprobantesMasivos />}
        {currentPage === 'cuentas-bancarias'       && permitidoMenu(user, 'cuentas-bancarias') && <CuentasBancarias />}
        {currentPage === 'polizas-contables'       && permitidoMenu(user, 'polizas-contables') && <PolizasContables />}
        {currentPage === 'reporte'                 && permitidoMenu(user, 'reporte') && <Reporte />}
        {currentPage === 'agentes-ia'              && permitidoMenu(user, 'agentes-ia') && <AgentesIA />}
        {currentPage === 'borradas'                && permitidoMenu(user, 'borradas') && <Papelera />}
        {currentPage === 'admin'                   && user.rol === 'admin'    && <Admin userRol="admin" />}
        {currentPage === 'catalogos'               && (user.rol === 'finanzas' || (user.catalogosPermitidos || []).length > 0)
          && <Admin userRol={user.rol} catalogosPermitidos={user.catalogosPermitidos || []} />}
        </div>
        <footer className="app-footer"><span>© {new Date().getFullYear()} Selectshop</span><span>Gestión de solicitudes y pagos</span></footer>
      </main>

      {/* ── Modal: seleccionar tipo de solicitud ───────────────────────────── */}
      {nuevaModal && (
        <div onClick={() => setNuevaModal(false)} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(17,17,17,0.45)', display: 'grid', placeItems: 'center', padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-labelledby="nueva-titulo" onClick={e => e.stopPropagation()} style={{
            background: 'var(--screws), var(--panel)', border: '1px solid var(--mesh)',
            borderRadius: 6, boxShadow: '0 24px 60px rgba(17,17,17,0.18)',
            padding: '24px 24px 20px', width: Math.min(windowW - 32, 680), maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
              <div>
                <div id="nueva-titulo" style={{ fontFamily: 'var(--label-font)', fontSize: 24, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink)', lineHeight: 1 }}>Nueva solicitud</div>
                <div style={{ fontSize: 13, color: 'var(--glass)', marginTop: 6 }}>Elige el tipo para continuar</div>
              </div>
              <button type="button" className="nx-ghost" onClick={() => setNuevaModal(false)} aria-label="Cerrar" style={{ width: 32, height: 32, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Icono nombre="cerrar" size={16} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
              {TIPOS_NUEVA.filter(t => permitidoMenu(user, t.id)).map(t => (
                <button type="button" key={t.id} className="nx-tipo" onClick={() => { handleNav(t.id); setNuevaModal(false); }}>
                  <span className="nav-icon" style={{ color: 'var(--plasma)', marginBottom: 10 }}><IconoMenu id={t.id} size={20} /></span>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>{t.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--glass)', lineHeight: 1.45 }}>{t.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Aviso del administrador: no se cierra haciendo clic afuera ───── */}
      {avisosPendientes.length > 0 && (() => {
        const aviso = avisosPendientes[0];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(17,17,17,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div role="alertdialog" aria-modal="true" aria-labelledby="aviso-titulo" style={{
              background: 'var(--screws), var(--panel)', border: '1px solid var(--plasma-line)', borderRadius: 6, width: 480, maxWidth: '100%',
              maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(17,17,17,0.18)', padding: '24px 26px',
            }}>
              <div className="nx-label" style={{ color: 'var(--plasma)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Icono nombre="aviso" size={16} /> Aviso de administración
              </div>
              <div id="aviso-titulo" style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink)', marginBottom: 10 }}>{aviso.titulo}</div>
              <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 20 }}>{aviso.mensaje}</div>
              <button type="button" className="nx-plate" onClick={marcarAvisoVisto} disabled={enviandoAviso} style={{ opacity: enviandoAviso ? 0.7 : 1 }}>
                {enviandoAviso ? 'Un momento…' : 'Ya lo leí'}
              </button>
              {avisosPendientes.length > 1 && (
                <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 10, textAlign: 'center' }}>
                  Tienes {avisosPendientes.length - 1} aviso{avisosPendientes.length - 1 !== 1 ? 's' : ''} más después de este
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function App() {
  return <AuthProvider><Layout /></AuthProvider>;
}
